import React, { useState, useRef, useEffect } from "react";
import { Form, Input, Button, Typography, message, Select } from "antd";
import { useNavigate } from "react-router-dom";
import { generateOTPAPI, verifyOTPAPI } from "feature/authv2/api/auth-api";
import { cookieExpiresFromToken, tokenManager } from "services/client/token-manager";
import { getUserAccountDetailsAPI } from "api/account";
import { PERMISSION_REFETCH_EVENT } from "providers/PermissionProvider";
import { encodeObject } from "shared/utils/encryption";
import { ENABLE_AUTO_LOGIN_AFTER_OTP_COMPLETE } from "feature/authv2/constants";
import { useGuestToken } from "app/providers/GuestTokenProvider";
import { useAccountDetails } from "app/providers/AccountDetailsProvider";

const PHONE_MAX_LENGTH = 10;
const PHONE_MIN_LENGTH = 10;
const OTP_LENGTH = 6;
const RESEND_OTP_COOLDOWN_SECONDS = 30;

export default function LoginWithOtp({ onSuccess, onOtpSent }) {
  const navigate = useNavigate();
  const otpInputRefs = useRef([]);
  const [phone, setPhone] = useState("");
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [otpSent, setOtpSent] = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [phoneError, setPhoneError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [roles, setRoles] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedHubId, setSelectedHubId] = useState(null);
  const { refreshGuestToken } = useGuestToken();
  /** Cleared when OTP becomes incomplete so the same 6 digits can auto-verify again after a failed attempt. */
  const autoVerifiedForOtpRef = useRef(null);
  const handleVerifyOtpRef = useRef(() => {});
  const { refreshAccountDetails } = useAccountDetails();

  // 30-second countdown before Resend OTP is available
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const formatPhoneInput = (value) => value.replace(/\D/g, "").slice(0, PHONE_MAX_LENGTH);

  const validatePhone = (value) => {
    const digits = value?.replace(/\D/g, "") || "";
    return digits.length >= PHONE_MIN_LENGTH && digits.length <= PHONE_MAX_LENGTH;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    setPhone(formatted);
    setPhoneError("");
  };

  const handleSendOtp = async () => {
    setPhoneError("");
    setOtpError("");
    if (!phone?.trim()) {
      setPhoneError("Please enter your phone number");
      return;
    }
    if (!validatePhone(phone)) {
      setPhoneError(`Enter a valid phone number (${PHONE_MIN_LENGTH}-${PHONE_MAX_LENGTH} digits)`);
      return;
    }
    setSendOtpLoading(true);
    try {
      await refreshGuestToken({ force: true });
      if (!tokenManager.getGuest()) {
        message.error("Could not start sign-in. Please refresh the page and try again.");
        return;
      }
      let otpResult = await generateOTPAPI({ phoneNo: phone.trim() });
      otpResult = otpResult?.data?.data;
      console.log("otpResult", otpResult);
      if (otpResult) {
        setRoles(otpResult?.roles || []);
        setHubs(otpResult?.hubs || []);
        setSelectedRoleId(null);
        setSelectedHubId(null);
      }
      setOtpSent(true);
      setResendCooldown(RESEND_OTP_COOLDOWN_SECONDS);
      setOtpDigits(Array(OTP_LENGTH).fill(""));
      onOtpSent?.();
      otpInputRefs.current[0]?.focus?.();
    } catch (err) {
      const description = err?.message || err?.data?.message || "Failed to send OTP";
      message.error(description);
    } finally {
      setSendOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setOtpError("");
    setResendLoading(true);
    try {
      await refreshGuestToken({ force: true });
      if (!tokenManager.getGuest()) {
        message.error("Could not refresh session. Please try again.");
        return;
      }
      await generateOTPAPI({ phoneNo: phone.trim() });
      setResendCooldown(RESEND_OTP_COOLDOWN_SECONDS);
      setOtpDigits(Array(OTP_LENGTH).fill(""));
      onOtpSent?.();
      otpInputRefs.current[0]?.focus?.();
    } catch (err) {
      const description = err?.message || err?.data?.message || "Failed to resend OTP";
      message.error(description);
    } finally {
      setResendLoading(false);
    }
  };

  const otpValue = otpDigits.join("");
  const isOtpComplete = otpValue.length === OTP_LENGTH;

  const handleOtpDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otpDigits];
    next[index] = digit;
    setOtpDigits(next);
    setOtpError("");
    if (digit && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus?.();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus?.();
      const next = [...otpDigits];
      next[index - 1] = "";
      setOtpDigits(next);
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData?.("text") || "").replace(/\D/g, "");
    if (!pasted) return;
    const digits = pasted.slice(0, OTP_LENGTH).split("");
    const next = Array(OTP_LENGTH).fill("");
    digits.forEach((d, i) => (next[i] = d));
    setOtpDigits(next);
    setOtpError("");
    const focusIndex = Math.min(digits.length, OTP_LENGTH) - 1;
    setTimeout(() => otpInputRefs.current[focusIndex]?.focus?.(), 0);
  };

  const handleVerifyOtp = async () => {
    setOtpError("");
    if (!isOtpComplete) {
      message.warning("Enter the OTP you received");
      return;
    }
    setVerifyLoading(true);
    try {
      const res = await verifyOTPAPI({
        phoneNo: phone.trim(),
        otp: otpValue,
        ...(selectedRoleId != null && { roleId: selectedRoleId }),
        ...(selectedHubId != null && { hubId: selectedHubId }),
      });
      if (res?.ok && res?.data) {
        const accountDetails = await persistLogin(res.data?.data);
        handleAfterSuccessfulOtpVerification(res.data?.data);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent(PERMISSION_REFETCH_EVENT));
        }
        // refreshGuestToken({ force: true });
        if (onSuccess) {
          onSuccess(accountDetails);
          return;
        }
        navigate("/", { replace: true });
      } else {
        throw new Error("Invalid response");
      }
    } catch (err) {
      let errorMessage = err?.error?.data?.data?.message;
      setOtpError(errorMessage);
      const description = err?.message || err?.data?.message || "Verification failed";
      message.error(description);
    } finally {
      setVerifyLoading(false);
    }
  };

  handleVerifyOtpRef.current = handleVerifyOtp;

  useEffect(() => {
    if (!ENABLE_AUTO_LOGIN_AFTER_OTP_COMPLETE) return;
    if (!otpSent || verifyLoading) return;
    if (!isOtpComplete) {
      autoVerifiedForOtpRef.current = null;
      return;
    }
    if (autoVerifiedForOtpRef.current === otpValue) return;
    autoVerifiedForOtpRef.current = otpValue;
    void handleVerifyOtpRef.current();
  }, [otpSent, isOtpComplete, verifyLoading, otpValue]);

  const saveAccountDetailsToCookie = (encryptedPayload, accessTokenForExp) => {
    if (!encryptedPayload) return;
    // TOKEN_PERSIST_DUMMY: OTP — accountDetails (tokenManager, expiry from access JWT)
    tokenManager.setAccountDetails(encryptedPayload, accessTokenForExp);
  };

  const persistLogin = async (data) => {
    const access = data.accessToken;
    const refresh = data.refreshToken ?? data.accessToken;
    const primaryAccountId = data?.user?.id;
    const userId = data?.user?.id;
    const refreshExpiry = cookieExpiresFromToken(refresh);
    if (access) {
      tokenManager.setAccess(access);
      tokenManager.setAuth(access);
    }
    if (refresh) {
      tokenManager.setRefresh(refresh);
    }
    if (primaryAccountId) {
      tokenManager.setPrimaryAccountId(primaryAccountId, refreshExpiry);
      tokenManager.setUserId(userId, refreshExpiry);
    }
    tokenManager.clearGuest();

    try {
      const detailsResult = await refreshAccountDetails({ refreshExpiry });
      if (!detailsResult.ok) {
        message.error("Failed to refresh account details. Please try again.");
        return null;
      }
      return detailsResult.accountDetails ?? null;
    } catch (err) {
      const msg = err?.message || err?.data?.message || "Could not load account details.";
      message.warning(msg);
      return null;
    }
  };

  const inputBorderRadius = 8;
  const otpInputBorder = otpError ? "#ff4d4f" : "#d9d9d9";

  const handleAfterSuccessfulOtpVerification = (data = {}) => {
    const { user, accessToken } = data;
    const { username, password } = user?.frappeCred || {};
    const frappepassword = password;
    const frapeUserName = username;
    const expiry = cookieExpiresFromToken(accessToken);
    if (frappepassword && frapeUserName) {
      tokenManager.setFrappeCredentials(frapeUserName, frappepassword, expiry);
    } else {
      tokenManager.clearFrappeCredentials();
    }
  };
  return (
    <Form layout="vertical" requiredMark={false}>
      <Form.Item
        label="Phone number"
        validateStatus={phoneError ? "error" : undefined}
        help={phoneError}
        style={{ marginBottom: 16, fontWeight: 500, fontSize: "14px", color: "#444444" }}
      >
        <Input.Group
          compact
          style={{
            display: "flex",
            alignItems: "stretch",
            border: "1px solid " + (phoneError ? "#ff4d4f" : "#d9d9d9"),
            borderRadius: inputBorderRadius,
            overflow: "hidden",
          }}
        >
          <Input
            type="tel"
            placeholder="Enter your phone number"
            value={phone}
            onChange={(e) => handlePhoneChange(e)}
            maxLength={PHONE_MAX_LENGTH}
            disabled={otpSent}
            bordered={false}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
            }}
          />
          {otpSent ? (
            resendCooldown > 0 ? (
              <span
                style={{
                  padding: "4px 12px",
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  color: "#8c8c8c",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Resend OTP in {resendCooldown}s
              </span>
            ) : (
              <Typography.Link
                onClick={handleResendOtp}
                disabled={resendLoading}
                style={{
                  padding: "4px 12px",
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  color: "#1E5EFF",
                  textDecoration: "underline",
                  fontSize: "14px",
                  fontWeight: 590,
                }}
              >
                {resendLoading ? "Sending…" : "Resend OTP"}
              </Typography.Link>
            )
          ) : (
            <Button
              type="link"
              loading={sendOtpLoading}
              onClick={handleSendOtp}
              style={{
                padding: "4px 12px",
                height: "auto",
                color: "#1890ff",
                fontWeight: 590,
                fontSize: "14px",
                color: "#1E5EFF",
              }}
            >
              Send OTP
            </Button>
          )}
        </Input.Group>
      </Form.Item>

      {otpSent && roles?.length >= 2 && (
        <Form.Item label="Role" style={{ marginBottom: 16 }}>
          <Select
            placeholder="Select role"
            value={selectedRoleId ?? undefined}
            onChange={setSelectedRoleId}
            allowClear
            options={roles.map((item) => ({
              value: item.id,
              label: item.name ?? item.id,
            }))}
            style={{ width: "100%" }}
          />
        </Form.Item>
      )}

      {otpSent && hubs?.length >= 2 && (
        <Form.Item label="Hub" style={{ marginBottom: 16 }}>
          <Select
            placeholder="Select hub"
            value={selectedHubId ?? undefined}
            onChange={setSelectedHubId}
            allowClear
            options={hubs.map((item) => ({
              value: item.id,
              label: item.name ?? item.id,
            }))}
            style={{ width: "100%" }}
          />
        </Form.Item>
      )}

      <Form.Item
        label="OTP"
        validateStatus={otpError ? "error" : undefined}
        help={otpError}
        style={{ marginBottom: 24 }}
      >
        <div style={{ display: "flex", gap: 8, justifyContent: "left" }}>
          {Array.from({ length: OTP_LENGTH }).map((_, i) => (
            <Input
              key={i}
              ref={(el) => (otpInputRefs.current[i] = el)}
              value={otpDigits[i]}
              onChange={(e) => handleOtpDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(i, e)}
              onPaste={handleOtpPaste}
              maxLength={1}
              disabled={verifyLoading}
              style={{
                width: 48,
                height: 48,
                textAlign: "center",
                fontSize: 18,
                fontWeight: 500,
                borderRadius: inputBorderRadius,
                borderColor: otpInputBorder,
              }}
              inputMode="numeric"
            />
          ))}
        </div>
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <Button
          block
          size="large"
          type="primary"
          loading={verifyLoading}
          disabled={!isOtpComplete || verifyLoading}
          onClick={handleVerifyOtp}
          style={{
            height: 44,
            borderRadius: inputBorderRadius,
            fontWeight: 600,
            fontSize: 14,
            textTransform: "uppercase",
            ...(isOtpComplete
              ? {
                  background: "#1E5EFF",
                  borderColor: "#1E5EFF",
                  color: "#fff",
                  ...(verifyLoading ? { opacity: 0.92 } : {}),
                }
              : { background: "#f5f5f5", borderColor: "#d9d9d9", color: "#8c8c8c" }),
          }}
        >
          {verifyLoading ? "Signing in…" : "Login"}
        </Button>
      </Form.Item>
    </Form>
  );
}
